"use client";

import PropTypes from "prop-types";
import CustomImage from "../../atoms/customImage";
import { useEffect, useRef, useState, useCallback } from "react";

/**
 * CountOnScroll component shows the number of rendered products
 * out of the total number of products while the user is scrolling.
 * It also acts as a "scroll to top" button when clicked.
 *
 * @component
 * @param {Object} props - Component props
 * @param {number} props.totalRenderedProducts - Number of products currently rendered on the page
 * @param {number} props.totalNoOfProducts - Total number of products available
 * @returns {JSX.Element|null} The scroll-to-top product count badge or null
 */
const CountOnScroll = ({ totalRenderedProducts, totalNoOfProducts }) => {
  const [isScroll, setIsScroll] = useState(false);
  const scrollTimeout = useRef(null);

  // Scroll to top handler
  const onScrollToTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  // Scroll listener with debounce
  const handleScroll = useCallback(() => {
    setIsScroll(true);

    if (scrollTimeout.current) clearTimeout(scrollTimeout.current);

    scrollTimeout.current = setTimeout(() => {
      setIsScroll(false);
    }, 1500);
  }, []);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (scrollTimeout.current) clearTimeout(scrollTimeout.current);
    };
  }, [handleScroll]);

  if (!isScroll) return null;

  return (
    <div
      onClick={onScrollToTop}
      className="fixed bottom-100 left-1/2 z-1 flex -translate-x-1/2 cursor-pointer items-center justify-center rounded-2xl bg-black-900 bg-opacity-70 p-8 text-12 text-fnp-100 md:bottom-120 md:left-auto md:right-20 md:translate-x-0 md:p-16 md:py-6 md:text-14"
    >
      <span className="mr-4">{`${totalRenderedProducts} / ${totalNoOfProducts}`}</span>
      <CustomImage src="/icons/count-arrow-up.svg" width={16} height={16} alt="arrow up" isPriority={false} />
    </div>
  );
};

CountOnScroll.propTypes = {
  totalRenderedProducts: PropTypes.number.isRequired,
  totalNoOfProducts: PropTypes.number.isRequired,
};

export default CountOnScroll;
